<template>
  <div v-if="url">
    <div class="widget">
      <div class="widget-body" v-if="!hasItem">
        <h4>{{'upgrade.step4Title' | translate}}</h4>
        <p>
          <a href="#" @click.prevent="regenerateInvoiceLink()">{{ $t('upgrade.step4') }}</a>
        </p>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="categories-container">
      <!-- Each step of the order in cascate -->
      <div class="datacenters-container" v-if="!hasItem">
        <h3 for="datacenter"><strong>{{$t('order.datacenters')}}</strong></h3>

        <VueSlickCarousel v-if="datacenters.length" v-bind="SliderSettings(datacenters.length)">
          <div v-for="(datacenter, index) in datacenters" :key="index" class="card card-datacenter"
               :class="{'active': datacenter.id === order.datacenter}"
               @click="selectDatacenter(datacenter.id)">
            <div class="body text-center d-flex flex-column align-items-center">
              <img class="card-img-top" :src="`${BASE_URL}/img/datacenter/${datacenter.image}`"
                   :alt="datacenter.name">
              <hr>
              <div>{{$t('order.'+datacenter.region_name)}}</div>
              <hr>
              <div>{{$t(datacenter.name)}}</div>
            </div>
          </div>
        </VueSlickCarousel>
      </div>

      <div class="categories-container" v-if="categories.length > 0  && !hasItem">
        <h3 for="category"><strong>{{$t('order.categories')}}</strong></h3>

        <multiselect
          :ref="orderRef('category')"
          v-model="selectedCategory"
          track-by="name"
          label="name"
          :placeholder="$t('order.select_categories_placeholder')"
          :options="categories">
          <template
            slot="singleLabel"
            slot-scope="{ option }">
            <strong>{{ option.name }}</strong> {{$t('order.selectedCategory')}}.
          </template>

        </multiselect>
      </div>

      <div class="products-container" v-if="products.length > 0" :ref="orderRef('product')">
        <h3 for="product"><strong v-show="!hasItem">{{$t('order.products')}}</strong></h3>
        <VueSlickCarousel v-if="products.length" v-bind="SliderSettingsProduct(products.length)">
          <div v-for="(product, index) in products" :key="index">
            <div class="card card-product"
                 :class="{'active': (product === selectedProduct ?  true : product.id === preSelectProductProp.id)}"
                 @click="selectProduct(product);selectDefaultProduct(product)"
            >
              <div class="pricing">
                <div class="price monthly">
                  <span class="cost" v-if="product.prices.length > 0">{{ formatPrice(product.prices) }}</span>
                  <span class="period">/{{(product === selectedProduct) ? $t('order.billing_cycle.' + selectedProduct.billing_cycle) : (updateDefaultCycle.billing_cycle !== 'Monthly')? $t('order.billing_cycle.' + updateDefaultCycle.billing_cycle) : $t('order.billing_cycle.Monthly')}}</span>
                </div>
                <div class="price p-name">
                  <span class="">{{ product.name }}</span>
                </div>
              </div>
              <div class="details" v-html="product.description">
              </div>
            </div>
          </div>
        </VueSlickCarousel>
      </div>

      <OrderBillingCycle
        v-if="selectedProduct.id && !hasItem"
        :defaultCyclefunction="originalPriceUpdate"
        v-on:cycleEvent="setBillingCycle($event) "
        :product="(!hasItem)? selectedProduct.id : null"
      />

      <div class="template-container" v-if="templates.length > 0  && !hasItem" :ref="orderRef('template')">
        <h3 for="templates"><strong>{{$t('order.templates')}}</strong></h3>

        <multiselect
          v-model="selectedTemplate"
          placeholder=""
          label="display_name"
          track-by="display_name"
          :options="templates"
          :option-height="42"
          :custom-label="templateCustomLabel"
          group-values="templates"
          group-label="type"
          :group-select="true"
          :autocomplete="false"
          :show-labels="false">
          <template slot="singleLabel" slot-scope="props">
            <img class="option__image" :src="BASE_URL + '/img/so/' + props.option.img" :alt="props.option.real_name">
            <span class="option__title">{{ props.option.display_name }} </span>
          </template>
          <template slot="option" slot-scope="props">
                <span v-if="props.option.display_name">
                  <img class="option__image" :src="BASE_URL + '/img/so/' + props.option.img"
                       :alt="props.option.real_name">
                  <span class="option__title">{{ props.option.display_name }}</span>
                </span>
            <span v-else>
                  <span class="option__title">{{ props.option.$groupLabel }}</span>
                </span>
          </template>
        </multiselect>
      </div>

      <div v-if="selectedTemplate && !hasItem && selectedTemplate.choose_password" class="template-password" ref="templatePassword">
        <h3><strong>{{$t('passwordStrength.passwordServer')}}</strong></h3>

        <VuePasswordStrength :badge="false" :secureLength="8"  v-on:password="setTemplatePassword($event)"/>
      </div>

      <!-- productSetings in order component -->
      <div class="product-settings-container" v-if="productSettings.length > 0" :ref="orderRef('productSettings')">
        <product-settings
          :is-upgrade="(item.id !== null)"
          :order="order"
          :productSettings="productSettings"
          @update="updateOrder"
        />
      </div>

      <!-- quantity, paymentMethod and continue-btn in order component -->
      <div class="continue-order-container" v-if="products.length > 0 && productSettings.length > 0">
        <div class="quantity" v-if="!hasItem" :ref="orderRef('quantity')">
          <h3 for="quantity"><strong>{{$t('order.quantity')}}</strong></h3>
          <vuestic-slider
            :options="{
                    min: 1,
                    max: 25,
                    interval: 1,
                    value: order.quantity,
                    tooltip: 'always',
                    tooltip_position: 'bottom',
                    tooltip_split: true,
                    formatter: function(value) {
                      return value;
                    }
                  }"
            v-model="order.quantity"
            @input="selectQuantity(order.quantity)"
          >
          </vuestic-slider>
        </div>
        <hr/>
        <div :ref="orderRef('paymentMethod')">
          <h3 class="mt-5">{{ $t('upgrade.paymentMethod') }}</h3>
          <div class="row">
            <div class="col-xs-12 col-md-12">
              <vuestic-simple-select
                :label="$t('upgrade.paymentMethod')"
                v-model="paymentMethodSelected"
                name="paymentMethod"
                :required="true"
                option-key="display_name"
                v-bind:options="paymentGateways"
              ></vuestic-simple-select>
            </div>
          </div>
        </div>

        <Recommendation v-on:recommendationIdEvent="recommendationReturn($event)"
                        v-if="categories.length > 0  && !hasItem"/>

        <button
          type="submit"
          v-if="!hasItem && !processing_order && !validationSession() && !userAs.exist"
          class="btn btn-success"
          @click="modalAuth()">
          {{$t('order.auth_order')}}
        </button>

        <div v-if="userAs.exist && !hasItem">
          {{$t('login.welcome')}}
          <div class="row mt-2">
            <div class="col-md-6">
              {{$t('order.auth_user_as')}} {{userAs.name}} ({{userAs.email}})
            </div>
            <div class="col-md-6 text-right">
              <a class="plain-link" href="#" @click.prevent="logout()">{{$t('user.logout')}}</a>
            </div>
          </div>
          <hr>
        </div>

        <button
          type="submit"
          v-if="!hasItem && !processing_order && validationSession() && userAs.exist"
          class="btn btn-success"
          @click="modalSendOrder()">
          {{$t('order.continue_order')}}
        </button>

        <DotsLoading v-if="processing_order"/>
      </div>

      <div v-if="loading">
        <div class="loading-box label"></div>
        <div class="loading-box"></div>
      </div>
    </div>

    <!-- purchase resume -->
    <transition name="ease">
      <div class="order" v-if="showResume">
        <Purchase :order="orderPurchase" @callSendOrder="modalSendOrder()"
                  @setDiscount="setDiscount" @setDiscountValue="setDiscountValue" :updateSettingsReload="updateSettingsReload" />
      </div>
    </transition>

    <!-- modal confirm order -->
    <vuestic-modal
      ref="modalOrder"
      :show.sync="modalOrder.show"
      v-bind:force="true"
      :cancelText="$t('modal.cancel')"
      :okText="$t('order.buy')"
      @ok="sendOrder()"
      @cancel="res => {this.processing_order = false}"
    >
      <div slot="title">{{ $t('order.continue_add_order') }}</div>
      <div class="product" v-if="modalOrder.order != null">
        <p v-if="modalOrder.order.category">
          <span>{{modalOrder.order.product.name}} - {{ formatPrice(modalOrder.order.product.prices) }}</span>
        </p>
        <p v-if="modalOrder.order.template"><span></span> <strong>{{modalOrder.order.template.display_name}}</strong>
        </p>
        <p v-if="discountValue"><strong>{{$t('order.discount')}}: {{discountValue}}</strong></p>

        <template v-if="modalOrder.order.settings.length > 0">

          <div v-for="(setting, index) in modalOrder.order.settings" :key="index">
            <strong>{{$t('order.' + setting.name)}}:</strong>
            <span v-if="setting.name == 'anti_ddos'">{{setting.value ? 'Premium' : setting.unity}}</span>

            <span v-if="setting.value > 0">{{ (setting.value === true)? $t('upgrade.'+setting.value) : setting.value }} {{setting.unity}}</span>
            <span v-else>{{$t('upgrade.false')}}</span>

            <span>{{ setting.price !== 0 ? setting.price : "" }}</span>
          </div>

          <hr/>
          <div>
            <p><strong>{{$t('order.quantity')}}: {{modalOrder.order.quantity}}</strong></p>
            <h3><strong>{{$t('order.total_order')}}: {{modalOrder.order.price}}</strong></h3>
          </div>

        </template>
      </div>
    </vuestic-modal>

    <!-- Login modal -->
    <vuestic-modal
      ref="loginModal"
      :hasFooter="false"
    >
      <div slot="title">{{'login.alreadyCustomer' | translate}}</div>

      <div>
        <a class="btn btn-lg btn-primary btn-block btn-purple login-btn" @click="registrationModal('open')">
          <i class="fa-solid fa-user-plus"></i> {{$t('login.register')}}
        </a>
        <a class="btn btn-lg btn-primary btn-block btn-white login-btn" @click="loginExistModal('open')">
          <i class="fa-solid fa-right-to-bracket"></i> {{$t('login.registeredCustomer')}}
        </a>
      </div>
    </vuestic-modal>

    <!-- Registration modal -->
    <vuestic-modal
      ref="registrationModal"
      :large="true"
      :hasFooter="true"
    >
      <div slot="title">{{'register.signup' | translate}}</div>
      <div>
        <Register v-on:updateAuthEvent="authReturn($event)"/>
      </div>
      <div slot="footer"><small>{{ $t('register.loginaccountsmall') }} <a class="va-link"
                                                                          @click="loginExistModal('open')">{{
        $t('register.loginaccount') }}</a></small></div>
    </vuestic-modal>

    <vuestic-modal
      ref="loginExistModal"
      :large="true"
      :hasFooter="true"
    >
      <div slot="title">{{'login.login' | translate}}</div>
      <div>
        <Login v-on:updateAuthEvent="authReturn($event)"/>
      </div>
      <div slot="footer">
        <small>{{ $t('login.newaccountsmall') }} <a class="va-link" @click="registrationModal('open')">{{
          $t('login.newaccount') }}</a></small></div>
    </vuestic-modal>

    <!-- Wati widged -->
    <WatiWidget/>
  </div>
</template>

<script>
  import {getAxios} from 'services/ajax'
  import store from '../../store'
  import configs from 'services/configs'
  import Purchase from './Purchase'
  import Register from '@/components/auth/forms/Register'
  import Login from '@/components/auth/forms/Login'
  import ProductSettings from './ProductSettings'
  import moment from 'moment'
  import Recommendation from "../recommendation/Recommendation";
  import OrderBillingCycle from "./OrderBillingCycle";
  import VuePasswordStrength from "../forms/form-elements/VuePasswordStrength";
  import { mapState, mapActions } from 'vuex';


  // VueSlick Carousel
  import VueSlickCarousel from 'vue-slick-carousel'
  import 'vue-slick-carousel/dist/vue-slick-carousel.css'
  // VueSlick Carousel optional style for arrows & dots
  import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'
  import {SliderSettings, SliderSettingsProduct} from "./SliderSettings"
  //wati
  import WatiWidget from '../ui/WatiWidget'
  import {
    setOrderSettingsFromUrl,
    setOrderProductSettingsFromUrl,
    setSoSettingsFromUrl,
    setCategoryLoadSettingsFromUrl,
    setSettingsFromUrl
  } from './AutoloadPlans'
  import iframeUtil from '../../services/iframeUtil'

  export default {
    name: 'Order',
    props: {
      item: {
        type: Object,
        default() {
          return {
            id: null,
          }
        }
      },
      offline: {
        type: Boolean,
        default() {
          return false
        }
      },
      showResume: {
        type: Boolean,
        default: true
      },
      preSelectProductProp: {
        type: Object,
        default() {
          return {}
        }
      }
    },
    components: {
      Purchase,
      ProductSettings,
      Register,
      Login,
      Recommendation,
      OrderBillingCycle,
      VueSlickCarousel,
      WatiWidget,
      VuePasswordStrength
    },
    data() {
      return {
        invoiceId: null,
        updateSettingsReload: false,
        discount: null,
        discountValue: 0,
        userAs: {
          exist: false,
          name: null,
          email: null
        },
        datacenters: [],
        categories: [],
        products: [],
        templates: [],
        productSettings: [],
        selectedTemplate: null,
        selectedProduct: {
          billing_cycle: 'Monthly'
        },
        updateDefaultCycle: {
          billing_cycle: 'Monthly'
        },
        selectedCategory: null,
        order: {
          datacenter: null,
          category: null,
          template: null,
          settings: [],
          quantity: 1,
          product: [],
          url: null,
          recommendation_id: null,
          billing_cycle: 'Monthly'
        },
        originalPrice: 0,
        originalPriceUpdate: false,
        orderPreValidator: {},
        recommendation_id: null,
        paymentGateways: [],
        paymentMethodSelected: '',
        loading_fields: false,
        processing_order: false,
        modalOrder: {
          show: false,
          order: null
        },
        BASE_URL: configs.BASE_URL,
        slider: {
          formatter: v => `${v}px`,
          min: 20,
          max: 40
        },
        templatePassword: null
      };
    },
    computed: {
      ...mapState('client', ['clientDetails']),
      orderPurchase() {
        return this.order;
      },
      hasItem() {
        return this.item.id != null
      },
      loading() {
        return this.loading_fields
      },
      url() {
        return this.order.url
      }
    },
    watch: {
      preSelectProductProp: {
        handler: function (val) {
          if(val){
            this.preSelectProduct(val)
          }
        },
        deep: true
      },
      selectedCategory(category, newVal) {
        if (category && category.id) {
          this.order.category = category
          this.products = []
          this.templates = []
          this.selectedTemplate = null
          this.order.product = null
          this.order.template = null
          this.order.settings = []
          this.productSettings = []
          this.order.billing_cycle = 'Monthly'
          this.getProducts(category.id)
        } else {
          this.order.category = null
          this.products = []
          this.templates = []
          this.order.settings = []
          this.order.product = null
          this.order.template = null
          this.selectedTemplate = null
          this.productSettings = []
          this.order.billing_cycle = 'Monthly'
        }
      },
      selectedProduct(product, newVal) {
        if (product && product.id) {
          this.order.product = product
          this.productSettings = []
          this.selectedTemplate = null
          this.order.settings = []
          this.productSettings = []

          this.order.template = null
          this.getTemplates(product.id)
          this.order.billing_cycle = 'Monthly'
        } else {
          this.templates = []
          this.selectedTemplate = null
          this.order.product = null
          this.order.settings = []
          this.productSettings = []

          this.order.template = null
          this.productSettings = []
          this.order.billing_cycle = 'Monthly'
        }
      },
      selectedTemplate(template, newVal) {
        if (template && template.id) {
          this.order.template = template
          this.productSettings = []
          this.order.settings = [];
          this.order.billing_cycle = 'Monthly'
          this.getProductSettings()
        } else {
          this.order.template = null
          this.productSettings = []
          this.order.billing_cycle = 'Monthly'
          this.order.settings = [];
        }
      },
      hasItem(hasItem) {
        if (hasItem === true) {

          this.order.datacenter = this.item.datacenter_id
          this.order.category = this.item.product.category
          this.order.template = this.item.template

          this.loading_fields = true

          getAxios().get(`/order/categoriesByDatacenter/${this.item.datacenter_id}`).then(response => {
            if (response.data.length > 0) {
              this.categories = response.data;
            } else {
              this.showToast(this.$t('order.errors.category_empty'), {
                icon: 'fa-remove',
                position: 'top-center',
                duration: 3000,
                fullWidth: false
              })
              return
            }

            this.order.category = this.item.product.category

          })
            .catch(error => {
              console.log(error)
            })

          getAxios().get(`/order/productsByCategoryWithCycle/${this.item.product.category.id}/cycle/${this.item.billingcycle}`).then(response => {
            if (response.data.length === 0) {
              this.showToast(this.$t('order.errors.products_empty'), {
                icon: 'fa-remove',
                position: 'top-center',
                duration: 3000,
                fullWidth: false
              })
              return
            }
            this.products = response.data;

            this.updateDefaultCycle.billing_cycle = this.item.billingcycle

            this.loading_fields = false

          })
            .catch(error => {
              console.log(error)
            })
        }
      },
      'order.settings': function (item) {
        this.$emit('new-item', {settings: item});
      },
      'productSettings': function (item) {
        this.$emit('new-item', {settings: item});
      },
      'order.category': function (item) {

        this.$emit('new-item', {category: item});
      },
      'order.product': function (item) {
        this.$emit('new-item', {product: item});
      },
      'order.template': function (item) {
        this.$emit('new-item', {template: item});
      },
      'order.price': function (item) {
        this.$emit('new-item', {product: item});
      },
      'order.billing_cycle': function (item) {
        this.$emit('new-item', {product: item});
      },
      paymentMethodSelected(paymentMethodSelected) {
        this.$emit('new-item', {paymentMethod: paymentMethodSelected});
      }
    },
    created() {
      this.getOrders();
      this.getPaymentGateways();
      this.currentUser();
    },
    methods: {
      ...mapActions('client', ['fetchClientDetails']),
      SliderSettings: SliderSettings,
      SliderSettingsProduct: SliderSettingsProduct,
      setOrderSettingsFromUrl: setOrderSettingsFromUrl,
      setOrderProductSettingsFromUrl: setOrderProductSettingsFromUrl,
      setSoSettingsFromUrl: setSoSettingsFromUrl,
      setCategoryLoadSettingsFromUrl: setCategoryLoadSettingsFromUrl,
      setSettingsFromUrl: setSettingsFromUrl,
      setTemplatePassword(val){
        this.templatePassword  = val
      },
      setSelectedCategory(el) {
        this.selectedCategory = el
      },
      setBillingCycle(cycle) {
        this.order.product.billing_cycle = cycle.cycle
        this.order.billing_cycle = cycle.cycle
        this.selectedProduct.billing_cycle = cycle.cycle

        this.order.product.prices = cycle.price
        this.order.product.discount = cycle.discount
        this.order.product.monthly = cycle.monthly

        this.updateSettingsReload = !this.updateSettingsReload
      },
      regenerateInvoiceLink() {
        this.showToast(this.$t('redirect'), {
            icon: 'fa-check',
            position: 'top-center',
            duration: 5000,
            fullWidth: false
        })
        this.generateSsoToken(this.invoiceId)
      },
      recommendationReturn(value) {
        console.log('recommendation_id', value)
        this.recommendation_id = value
      },
      currentUser() {
        getAxios().get('user')
          .then(response => {
            this.userAs.exist = true
            this.userAs.name = `${response.data.WhmcsAuthUser.data.first_name} ${response.data.WhmcsAuthUser.data.last_name}`
            this.userAs.email = response.data.WhmcsAuthUser.data.email
          })
          .catch(err => {
            localStorage.removeItem('token')
            localStorage.removeItem('X-refresh-token')

            this.userAs.exist = false
            this.userAs.name = null
            this.userAs.email = null
          })
          .finally(() => {
          });
      },
      authReturn(value) {
        let authValue = value

        this.$refs.registrationModal.cancel()
        this.$refs.loginModal.cancel()
        this.$refs.loginExistModal.cancel()

        if (authValue.result === 'success') {
          this.userAs.exist = true
          this.userAs.name = authValue.user.name
          this.userAs.email = authValue.user.email

          localStorage.setItem('token', authValue.token.accessToken)
          localStorage.setItem('region', authValue.token.region)
          localStorage.setItem('X-refresh-token', authValue.token.refreshToken)
          localStorage.setItem('userid', authValue.userid)

          // Fetch client details
          this.fetchClientDetails()

          this.showToast(this.$t('login.welcome'), {
            icon: 'fa-check',
            position: 'top-center',
            duration: 5000,
            fullWidth: false
          })

          return true
        }

        return false
      },
      orderRef(el) {
        // This method is used to get the order reference from the local storage and set it to the order object
        if (localStorage.order_date) {
          let now = moment();
          let order_date = moment(JSON.parse(localStorage.order_date).date);

          if (now.diff(order_date, 'days') <= 1) {
            const order = JSON.parse(localStorage.orders).order;
            const paymentMethod = JSON.parse(localStorage.orders).paymentMethod;

            switch (el) {
              case 'datacenter':
                if (order.hasOwnProperty('datacenter')) {
                  this.selectDatacenter(order.datacenter);
                  delete order.datacenter;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                }
                break;
              case 'category':
                if (order.hasOwnProperty('category')) {
                  this.selectedCategory = order.category;
                  delete order.category;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                }
                break;
              case 'product':
                if (order.hasOwnProperty('product')) {
                  this.selectedProduct = order.product;
                  delete order.product;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                  window.scrollTo(0, document.body.scrollHeight);
                }
                break;
              case 'template':
                if (order.hasOwnProperty('template')) {
                  this.selectedTemplate = order.template;
                  delete order.template;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                  window.scrollTo(0, document.body.scrollHeight);
                }
                break;
              case 'productSettings':
                if (order.hasOwnProperty('settings')) {
                  this.productSettings = order.settings;
                  this.order.settings = order.settings;
                  delete order.settings;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                  window.scrollTo(0, document.body.scrollHeight);
                }
                break;
              case 'paymentMethod':
                if (paymentMethod.hasOwnProperty('paymentMethod')) {
                  this.paymentMethodSelected = paymentMethod.paymentMethod;
                  delete paymentMethod.paymentMethod;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                  window.scrollTo(0, document.body.scrollHeight);
                }
                break;
              case 'quantity':
                if (order.hasOwnProperty('quantity')) {
                  this.order.quantity = order.quantity;
                  delete order.quantity;
                  localStorage.setItem('orders', JSON.stringify({order, paymentMethod}))
                  window.scrollTo(0, document.body.scrollHeight);
                }
                break;
              default:
                return null;
            }

          }
        }
      },
      registrationModal(action) {
        switch (action) {
          case 'open':
            this.$refs.loginExistModal.cancel()
            this.$refs.loginModal.cancel()
            this.$refs.registrationModal.open()
            break;
          case 'close':
            break;
          default:
        }
      },
      loginExistModal(action) {
        switch (action) {
          case 'open':
            this.$refs.registrationModal.cancel()
            this.$refs.loginModal.cancel()
            this.$refs.loginExistModal.open()
            break;
          case 'close':
            break;
          default:
        }
      },
      selectDefaultProduct(value) {
        this.originalPrice = value.prices
      },
      selectProduct(value) {
        /**
         * set default price with product update
         * @type {number}
         */
        if (this.originalPrice !== 0 && this.order.product !== null) {
          this.order.product.prices = this.originalPrice

          this.originalPriceUpdate = true
        }

        this.selectedProduct = value
        this.selectedProduct.billing_cycle = 'Monthly'
        this.selectedProduct.monthly = 1
      },
      selectQuantity(value) {
        this.order.quantity = value

        this.updateSettingsReload = !this.updateSettingsReload
      },
      preSelectProduct(product){
        this.selectProduct(product)
        this.selectDefaultProduct(product)
      },
      templateCustomLabel({display_name, desc}) {
        return `${display_name} – ${desc}`
      },
      getOrders() {
        store.commit('setLoading', true)
        getAxios().get('/order/datacenters/list').then(response => {
          this.datacenters = response.data;

          this.setOrderSettingsFromUrl()
        })
          .catch(error => {
            console.log(error)
          })
          .finally(() => {
            store.commit('setLoading', false)
          });
      },
      formatPrice(prices) {
        let price = 0.0;
        let objPrice = {};

        if (prices.length > 0) {
          objPrice = prices[0]

          if (!(objPrice === null || objPrice === undefined)) {
            price = objPrice.price

            if (objPrice.region_id == 1) {
              price = price.toLocaleString("pt-BR", {style: "currency", currency: "BRL"});
            } else {
              price = price.toLocaleString("en-US", {style: "currency", currency: "USD"});
            }
          }
        }

        return price
      },
      getProducts(categoryId) {
        this.loading_fields = true
        getAxios().get(`/order/productsByCategory/${categoryId}/datacenter/${this.order.datacenter}`).then(response => {
          if (response.data.length === 0) {
            this.showToast(this.$t('order.errors.products_empty'), {
              icon: 'fa-remove',
              position: 'top-center',
              duration: 3000,
              fullWidth: false
            })
            return
          }
          this.products = response.data;

          this.setOrderProductSettingsFromUrl()
        })
          .catch(error => {
            console.log(error)
          })
          .finally(() => {
            this.loading_fields = false
          });
      },
      selectDatacenter(id) {
        if (this.order.datacenter !== id) {
          this.order.datacenter = id;
          this.loading_fields = true
          getAxios().get(`/order/categoriesByDatacenter/${id}`).then(response => {
            if (response.data.length > 0) {
              this.categories = response.data;
              this.order.category = null;
              this.order.template = null;
              this.order.settings = [];
              this.products = [];
              this.templates = [];
              this.selectedTemplate = null;
              this.selectedProduct = {
                billing_cycle: 'Monthly'
              };
              this.selectedCategory = null;

              this.setCategoryLoadSettingsFromUrl();
            } else {
              this.showToast(this.$t('order.errors.category_empty'), {
                icon: 'fa-remove',
                position: 'top-center',
                duration: 3000,
                fullWidth: false
              })
              return
            }
          })
            .catch(error => {
              console.log(error)
            })
            .finally(() => {
              this.loading_fields = false
            });
        }
        ;
      },
      getTemplates(productId) {
        this.loading_fields = true
        getAxios().get(`/order/templatesByProduct/${productId}`).then(response => {
          this.templates = response.data.templates.map(template => {
            return template
          })
          this.setSoSettingsFromUrl()

          if (this.hasItem) {
            this.selectedTemplate = this.item.template
          }
          this.loading_fields = false
        });
      },
      getProductSettings() {
        this.loading_fields = true

        if (this.item && this.item.settings && this.item.settings.length > 0) {
          this.storage = this.item.settings.filter(setting => {
            return setting.product_settings.name == 'storage'
          })[0].value
        }

        getAxios().get(`/order/product/${this.order.product.id}/template/${this.order.template.id}`).then(response => {
          response.data = Object.values(response.data)
          this.productSettings = response.data.filter(item => item.hidden == 0)

          let storage = this.storage;

          this.productSettings = this.productSettings.map(item => {

            let min = item.min_value
            let max = item.max_value

            /** upgrate min storage set */
            if (item.name == 'storage' && storage) {
              item.min_value = storage
            }

            if(min > storage){
              item.min_value = min
            }

            for (let index = min; index <= max; index = index + item.step) {
              if (Array.isArray(item.options)) {
                item.options.push(index)
              } else {
                item.options = [index]
              }
            }

            if (item.type == 'toggle') {
              item.options = [false, true]
              item.min_value = false
              item.max_value = true
            }

            return item
          })

          this.productSettings
            .forEach(item => {
              item.value = item.min_value
              this.order.settings.push(item)
            });

          /**
           * Upgrade
           *
           * pre-selected settings
           */
          if (this.item.id != null && this.item.product.name === this.selectedProduct.name) {
            let objeto1 = this.item.settings
            let objeto2 = this.productSettings

            for (let elemento1 of objeto1) {
              const elemento2 = objeto2.find(e => e.id === elemento1.product_settings.id);

              if (elemento2) {
                elemento2.value = elemento1.value;
              }
            }
          }

          this.setSettingsFromUrl()

          this.updateSettingsReload = !this.updateSettingsReload

          this.loading_fields = false
        });
      },
      updateOrder(setting) {
        if (this.order.settings.length > 0) {
          let index = this.order.settings.findIndex(item => item.name === setting.name)
          if (index > -1) {
            this.order.settings.splice(index, 1, setting)
          } else {
            this.order.settings.push(setting)
          }
        } else {
          this.order.settings.push(setting)
        }

        this.updateSettingsReload = !this.updateSettingsReload
      },
      getPaymentGateways() {
        this.loading_fields = true
        getAxios().get(`customer/whmcs/get/payment-methods`).then(response => {
          this.paymentGateways = response.data
          this.loading_fields = false
        });
      },

      validationSession() {
        let sessionUser = localStorage.getItem('token')
        let sessionRegion = localStorage.getItem('region')

        if (sessionUser && sessionRegion) {
          return true
        }

        return false;
      },

      logout() {
        localStorage.removeItem('token')
        localStorage.removeItem('X-refresh-token')
        localStorage.removeItem('region')
        localStorage.removeItem('userid')

        this.userAs.exist = false
        this.userAs.name = null
        this.userAs.email = null

        this.showToast(this.$t('login.logout'), {
          icon: 'fa-check',
          position: 'top-center',
          duration: 5000,
          fullWidth: false
        })
      },

      modalAuth() {
        if (!this.validationSession()) {
          this.$refs.loginModal.open();
          return;
        }

        this.modalSendOrder()
      },

      setDiscount(event) {
        this.discount = event
      },
      setDiscountValue(event) {
        this.discountValue = event
      },
      modalSendOrder() {
        // Is logged in
        if (!this.validationSession()) {
          this.$refs.loginModal.open();
          return;
        }

        if(this.selectedTemplate.choose_password && !this.templatePassword){
          this.showToast(this.$t('passwordStrength.choosePassword'), {
            icon: 'fa-exclamation',
            position: 'top-center',
            duration: 3000,
            fullWidth: false
          })

          this.$refs.templatePassword.scrollIntoView({ behavior: 'smooth' });

          return;
        }

        if (
          this.order.product && this.order.product.id &&
          this.paymentMethodSelected && this.paymentMethodSelected &&
          this.order.settings) {

          this.modalOrder.order = this.order
          this.processing_order = true

          getAxios()
          .post(`order/calculate_prices`, {
              order: this.modalOrder.order,
              coupon: this.discountValue
            })
            .then(response => {
              this.modalOrder.order.price = response.data.OrderCalculate.data.amount;

              this.$refs.modalOrder.open()
            })
            .catch(err => {
              this.showToast(this.$t('global_error.internal'), {
                icon: 'fa-exclamation',
                position: 'top-center',
                duration: 3000,
                fullWidth: false
              })
            })
            .finally(res => {
              this.processing_order = false
            });
        } else {
          this.showToast(this.$t('order.payment_select'), {
            icon: 'fa-exclamation',
            position: 'top-center',
            duration: 3000,
            fullWidth: false
          })
          return false
        }

      },
      async generateSsoToken(idInvoice) {
        const formData = new FormData()

        iframeUtil.sendEventToParent('redirect', {
          invoiceId: idInvoice,
        })

        formData.append('destination', `viewinvoice.php?id=${idInvoice}`)

        getAxios().post('sso', formData)
          .then(response => {
            let url = response.data.WhmcsAutoLoginClient.data.redirect_url

            this.order.url = url

            window.location.href = url
          })
          .catch(err => {
            this.showToast(this.$t('global_error.internal'), {
              icon: 'fa-exclamation',
              position: 'top-center',
              duration: 3000,
              fullWidth: false
            })

            return false
          })
          .finally(() => {
            store.commit('setLoading', false)
          });
      },
      async sendOrder() {
        store.commit('setLoading', true)
        //
        // if (this.offline) {
        //   let now = moment();
        //   localStorage.setItem('orders', JSON.stringify({order: this.order, paymentMethod: this.paymentMethodSelected}))
        //   localStorage.setItem('order_date', JSON.stringify({date: now.format('YYYY-MM-DD HH:mm:ss')}))
        //   this.$router.push(`/notlogged`);
        // }

        this.order.price = parseFloat(this.order.price.replace(/[^0-9,.]/g, '').replace(/[.]/g, '').replace(',', '.'));

        // Google Ads user data
        gtag('set', 'user_data', {
            "email": this.clientDetails.client.email,
            "phone_number": this.clientDetails.client.phonenumberformatted,
            "address": {
              "first_name": this.clientDetails.client.firstname,
              "last_name": this.clientDetails.client.lastname,
              "street": this.clientDetails.client.address1,
              "city": this.clientDetails.client.city,
              "region": this.clientDetails.client.state,
              "postal_code": this.clientDetails.client.postcode,
              "country": this.clientDetails.client.country
            }
        });
        // Google Ads conversion tracking
        gtag('event', 'conversion', {
          'send_to': 'AW-966588570/X1CaCPbIp18QmvHzzAM',
          'value': this.order.price,
          'currency': 'BRL',

          'product_name': this.order.product.name,
          'order_amount': this.order.price,
          'client_id': `${this.clientDetails.client.client_id}`,
          'billing_cycle': this.order.billing_cycle
        });

        // Microsoft Ads conversion tracking
        window.uetq=window.uetq||[];
        window.uetq.push('set', { 'pid': {
          'em': this.clientDetails.client.email,
          'ph': this.clientDetails.client.phonenumberformatted
        }});
        window.uetq.push("event", "purchase", {"revenue_value": this.order.price,"currency":"BRL"});

        this.order.recommendation_id = this.recommendation_id
        this.order.software_password = this.templatePassword

        await getAxios().post(`order/add`, {
          order: this.order,
          paymentMethod: this.paymentMethodSelected,
          coupon: this.discountValue
        })
          .then(async response => {
            if (response.data.invoice_id) {
              this.invoiceId = response.data.invoice_id;

              await this.generateSsoToken(response.data.invoice_id)
            } else {
              this.showToast(this.$t('global_error.internal'), {
                icon: 'fa-remove',
                position: 'top-center',
                duration: 3000,
                fullWidth: false
              })

              store.commit('setLoading', false)
            }
          })
          .catch(err => {
            this.showToast(this.$t('global_error.internal'), {
              icon: 'fa-exclamation',
              position: 'top-center',
              duration: 3000,
              fullWidth: false
            })

            return false

            store.commit('setLoading', false)
          })
      },
    }
  }
</script>
<!-- multi-select -->
<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>
<style scoped>
  .ease-enter-active,
  .ease-leave-active {
    transition: all 1s ease;
  }

  .ease-enter,
  .ease-leave-to {
    opacity: 0;
  }

  .quantity > h3 {
    margin: 0;
    margin-bottom: 40px;
  }

  .widget-body {
    width: 50%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .template-password {
    margin-bottom: 15px !important;
    margin-top: 25px !important;
  }

  @media only screen and (max-width: 600px) {
    .widget-body {
      width: 100%;
    }
  }

  .widget-body > div {
    margin-bottom: 38px;
  }

  .continue-order {
    margin-top: 20px;
    width: 100%;
  }

  .continue-order > button {
    width: 100%;
  }

  .datacenters-container {
    display: flex;
    flex-direction: column;
  }

  .datacenters-container-group {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .title {
    margin-top: 5px;
    text-align: center;
    font-size: 1em;
  }

  .pricing {
    border-bottom: 1px solid #dfdfdf;
    padding: 18px 0 11px;
  }

  .aurora-size .monthly .dollar {
    display: inline-block;
    font-size: 14px;
    line-height: 15px;
    vertical-align: top;
    position: relative;
    left: 5px;
    top: 0;
  }

  .monthly .cost {
    display: block;
    padding-bottom: 5px;
    font-size: 26px;
  }

  .monthly .period {
    font-size: 14px;
    font-style: italic;
    font-weight: bold;
  }

  .p-name {
    font-size: 14px;
  }

  .price {
    text-align: center;
    line-height: 18px;
    margin-bottom: 5px;
  }

  .details {
    padding: 15px 0;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    text-align: center;
  }

  .details span {
    display: block;
    margin: 0;
    color: #bbb;
  }

  .card {
    width: 150px;
    height: auto;
    padding: 10px;
    margin: 10px;
  }

  .card-product {
    width: 80%;
    min-height: 260px;
    margin: 10px;
    padding: 0px;
  }

  .card:not(.active):hover {
    background-color: #f5f5f5;
    cursor: pointer;
  }

  .card.active {
    border-color: var(#333);
    box-shadow: black;
    box-shadow: 2px 2px 10px 0px #333;
  }

  .option__small {
    font-size: 0.75em;
    margin-left: 5px;
  }

  .option__desc {
    display: flex;
    justify-content: space-between;
  }

  .option__image {
    width: 20px;
    height: 20px;
    margin-right: 5px;
  }

  .my-select {
    background-color: #ededed;
    color: #5F6162;
    border: 0 none;
    border-radius: 1px 20px 20px 1px;
    height: 42px;
    width: 200px;
    padding: 6px 20px;
  }

  .reinstall {
    display: flex;
  }

  .loading-box {
    height: 42px;
    position: relative;
    background-image: linear-gradient(to right, #EEE 0%, #FAFAFA 20%, #EEE 100%);
    background-color: #EEE;
    animation: 1s shimmer infinite linear;
  }

  .loading-box.label {
    width: 200px;
    height: 16px;
    margin-top: 50px;
    margin-bottom: 10px;
  }

  .card-datacenter {
    width: calc(100% - 20px) !important;
  }

  .card-datacenter hr {
    width: 100%;
  }

  .card-datacenter img {
    pointer-events: none;
  }

  @-webkit-keyframes shimmer {
    0% {
      background-position: 0px 0;
    }
    100% {
      background-position: 600px 0;
    }
  }

  @-moz-keyframes shimmer {
    0% {
      background-position: 0px 0;
    }
    100% {
      background-position: 600px 0;
    }
  }

  @-o-keyframes shimmer {
    0% {
      background-position: 0px 0;
    }
    100% {
      background-position: 600px 0;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 0px 0;
    }
    100% {
      background-position: 600px 0;
    }
  }

  .widget-body {
    padding-bottom: 50px;
  }

  @media only screen and (max-width: 992px) {
    .widget-body {
      width: 100%;
    }

    h3 {
      font-size: 1.35em;
    }

    .card {
      width: 90%;
    }
  }

  .carousel slide {
    max-height: 100px;
    width: auto;
    max-width: 100%;
  }

  .card-img-top {
    max-width: 100px;
  }

  .categories-container > div:nth-child(even) {
    margin-top: 2.5rem;
    margin-bottom: 7.5rem;
  }

  .continue-order-container {
    margin-top: 2.5rem;
    margin-bottom: 8.5rem;
  }

  .categories-container {
    margin-top: 2.5rem;
    margin-bottom: 7.5rem;
  }

  .products-container {
    margin-top: 2.5rem;
    margin-bottom: 7.5rem;
  }

  .VueCarousel {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  h3 {
    line-height: 1.2;
  }

  /* Login Btn */
  .login-btn.btn-white {
    color: #7643C9;
    background: #fff;
  }

  .login-btn.btn-white:hover {
    color: #fff;
    background: #420679;
  }

  .login-btn.btn-purple {
    background: #7643C9;
  }

  .login-btn.btn-purple:hover {
    background: #420679;
  }

  small a:not([href]):not([tabindex]) {
    color: #34495e;
  }
</style>

<style>
  button.slick-prev:before, button.slick-next:before {
    color: #363636 !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/order/Order.vue